import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';
import { controlClasses, describedBy, Field } from './Field';

// `prefix` is a legitimate (if obscure) HTML attribute typed as string — we shadow
// it with a ReactNode adornment, so it has to be omitted from the base props.
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Rendered inside the control on the left (icon, ₹, +91). */
  prefix?: React.ReactNode;
  /** Rendered inside the control on the right (unit, action button). */
  suffix?: React.ReactNode;
  inputSize?: 'sm' | 'md' | 'lg';
  containerClassName?: string;
}

const SIZES = {
  sm: 'h-8 text-sm',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    prefix,
    suffix,
    inputSize = 'md',
    className,
    containerClassName,
    id,
    required,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const invalid = Boolean(error);

  return (
    <Field
      label={label}
      htmlFor={inputId}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        {prefix ? (
          <span className="pointer-events-none absolute left-2.5 flex items-center text-fg-subtle">
            {prefix}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(inputId, invalid, Boolean(hint))}
          className={cn(
            controlClasses,
            SIZES[inputSize],
            prefix && 'pl-8',
            suffix && 'pr-9',
            className,
          )}
          {...props}
        />
        {suffix ? (
          <span className="absolute right-2.5 flex items-center text-fg-subtle">{suffix}</span>
        ) : null}
      </div>
    </Field>
  );
});
